import { useEffect, useState } from "react";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";

import { useUserBundleAccess } from "@/hooks";
import { usePagination } from "@/hooks/usePagination";
import { useNavigateModal } from "@/router/useNavigateModal";
import type { Status } from "@/types";
import { Pagination, Table } from "@/ui";
import { getTableColumns } from "./UserBundleAccessTableColumns";

interface UserBundleAccessTableProps {
  searchQuery: string;
  selectedBundles: string[];
  approvedTab?: boolean;
  selectedRadioValue: string[];
}

export const UserBundleAccessTable = ({
  searchQuery,
  selectedBundles,
  approvedTab = false,
  selectedRadioValue,
}: UserBundleAccessTableProps) => {
  const navigateModal = useNavigateModal();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { pagination, setPageIndex, onPaginationChange } = usePagination();
  const { useAllUserBundleAccess } = useUserBundleAccess();
  const { data: userBundleAccessData, isLoading } = useAllUserBundleAccess(
    pagination.pageIndex + 1,
    searchQuery,
    selectedBundles,
    selectedRadioValue as Status[],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, selectedBundles, selectedRadioValue, setPageIndex]);

  const table = useReactTable({
    data: userBundleAccessData?.data ?? [],
    columns: getTableColumns(approvedTab, navigateModal),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: userBundleAccessData?.pagination?.totalPages ?? 1,
    state: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: userBundleAccessData?.pagination?.perPage ?? 10,
      },
      sorting,
    },
    onPaginationChange,
    onSortingChange: setSorting,
  });

  return (
    <div>
      <Table table={table} isLoading={isLoading} />
      <div className="flex items-center justify-between gap-3 rounded-b-2xl border border-neutral-200 px-4">
        <span className="text-sm text-neutral-500">
          {userBundleAccessData?.pagination?.total ?? 0}{" "}
          {userBundleAccessData?.pagination?.total === 1 ? "user" : "users"}
        </span>
        <Pagination
          currentPage={pagination.pageIndex + 1}
          totalPages={userBundleAccessData?.pagination?.totalPages ?? 1}
          onPageChange={(page) => setPageIndex(page - 1)}
        />
      </div>
    </div>
  );
};
